import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { App, Button, Dropdown, Skeleton, Spin } from 'antd';

import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { buildActionMenu } from '../common/actionMenu';
import ShareModal from '../common/ShareModal';
import SortableList from '../common/SortableList';
import { buildCollectionShareUrl } from '../../utils/shareUrls';
import { collectionCardsPath } from '../../utils/listLinks';
import { formatCount, joinMeta } from '../../utils/counts';
import { slugify } from '../../utils/strings';
import { normalizeId } from '../../utils/ids';
import { parentOf } from '../../utils/collectionFamily';
import { canAddSubCollection } from '../../utils/familyActions';
import NewSubCollectionModal from './NewSubCollectionModal';
import { roleLabel, canManage, isGlobalConsumer } from '../../utils/permissions';
import { publicationRefusal, unfollowCollection } from '../../services/publication';
import { isPersonal, kindLabel } from '../../utils/collectionKinds';
import { useFavorites } from '../../contexts/FavoritesContext';
import { CollectionFavoriteButton } from './CollectionActions';
import { BOOKMARK, KEBAB } from '../icons';
import useRecordCodesVersion from '../../hooks/useRecordCodes';

const CollectionCardSkeleton = () => (
	<div className="collection-card collection-card--skeleton">
		<Skeleton.Button active size="small" style={{ width: '40%' }} />
		<Skeleton active title={false} paragraph={{ rows: 2 }} style={{ marginTop: 12 }} />
	</div>
);

/**
 * The collections index grid: uniform 128px tiles. Identity = the 24px chip
 * beside a plain 14px/600 name (the tile is the collection's OWN surface, so
 * no bordered badge) plus a 3px left edge in the collection's hue — nowhere
 * else. Description clamps to two lines; one meta line at the foot says the
 * counts as words ("21 cards · 94 items"), the collection's kind tag
 * (Personal · Global · Shared — utils/collectionKinds.js), the role only when
 * it is not Owner, and "Empty · Add a card" when there is nothing to count.
 *
 * SUB-COLLECTIONS (docs/plans/sub-collections.md §5.1): a sub-collection's
 * meta line leads with its parent as an `xs` badge; a parent's leads with
 * "N sub-collections" (a door to its home's Sub-collections section) and
 * counts its family's items. `family` is `{ subCounts: Map<parentId, n>,
 * visibleIds: Set }` over the whole list the page loaded — a parent the
 * viewer cannot see is a plain badge, not a link.
 */
export default function CollectionList({ data = [], showPlaceholder = false, countsMap = {}, onUnfollowed, family = null }) {
	const skeletons = showPlaceholder ? Array.from({ length: 6 }) : [];
	return (
		<div className="collection-card-grid">
			{showPlaceholder
				? skeletons.map((_, idx) => <CollectionCardSkeleton key={`collection-skeleton-${idx}`} />)
				: data.map((collection, idx) => (
					<CollectionCard
						key={collection?._id || collection?.id || idx}
						collection={collection}
						counts={countsMap[collection?._id] || countsMap[collection?.id]}
						onUnfollowed={onUnfollowed}
						family={family}
					/>
				))}
			{!showPlaceholder && (!data || data.length === 0) ? (
				<div className="collection-card-grid__empty">
					No collections yet.
				</div>
			) : null}
		</div>
	);
}

/**
 * The compact form of the same list (the Favorites section): one 44px row
 * per collection — chip · name · counts — six or so across, so a handful of
 * favorites never orphans a two-tile row above the full grid.
 *
 * `reordering` (the section's Reorder button) turns it into a SortableList
 * of the same rows, each with a grip; `onReorder(collectionIds)` saves the
 * ONE favorites order Home and the rail read too.
 */
export function CollectionStrip({ data = [], countsMap = {}, reordering = false, onReorder }) {
	const items = useMemo(() => (data || []).map((collection, idx) => ({
		id: normalizeId(collection?._id || collection?.id) || `row-${idx}`,
		collection,
	})), [data]);
	if (!items.length) return null;

	const renderRow = ({ id, collection }) => {
		const counts = countsMap[id] || {};
		const meta = counts.loading === false
			? joinMeta(formatCount(counts.cards, 'card'), formatCount(counts.familyItems ?? counts.items, 'item'))
			: '';
		return (
			<Link to={id ? `/collections/${id}/items` : '#'} className="collection-strip__row" style={collection?.color ? { '--collection-accent': collection.color } : undefined}>
				<RecordBadge
					kind="collection"
					record={collection}
					variant="plain"
					size="xs"
					tileSize={27}
					title={collection?.name || 'Untitled collection'}
					className="collection-strip__title"
				/>
				{meta ? <span className="collection-strip__meta meta-line">{meta}</span> : null}
			</Link>
		);
	};

	if (reordering && typeof onReorder === 'function') {
		return (
			<SortableList
				items={items}
				layout="grid"
				ariaLabel="Favorite collections"
				className="collection-strip collection-strip--sortable"
				getLabel={item => item.collection?.name || 'collection'}
				onReorder={onReorder}
				renderItem={(item, { handle }) => (
					<div className="collection-strip__sortable">
						{handle}
						{renderRow(item)}
					</div>
				)}
			/>
		);
	}

	return (
		<div className="collection-strip">
			{items.map(item => <React.Fragment key={item.id}>{renderRow(item)}</React.Fragment>)}
		</div>
	);
}

/**
 * The foot of a tile: counts as words, each count that names other records a
 * door to them (the cards count opens the cards list filtered to this
 * collection); nothing at zero — an empty collection says "Empty · Add a
 * card" instead, linking to the Card library. The links are siblings of the
 * tile's stretched link, layered above it, so a click lands here.
 */
const CollectionMeta = ({ collection, counts, collectionId, collectionName, family }) => {
	const loading = counts?.loading !== false;
	if (loading) {
		return <Spin size="small" className="collection-card__meta-spinner" aria-label="Loading count" />;
	}
	const cardsCount = typeof counts.cards === 'number' ? counts.cards : 0;
	// A parent counts its family: its own items and its sub-collections'.
	const itemsCount = typeof counts.familyItems === 'number' ? counts.familyItems
		: typeof counts.items === 'number' ? counts.items : 0;
	const parent = parentOf(collection);
	const parentNode = parent ? (
		<RecordBadge
			kind="collection"
			record={parent}
			title={parent.name}
			size="xs"
			to={family?.visibleIds?.has(parent._id) ? `/collections/${parent._id}/` : undefined}
			className="collection-card__parent"
			aria-label={`Part of ${parent.name}`}
		/>
	) : null;
	const subCount = family?.subCounts?.get(normalizeId(collectionId)) || 0;
	const subText = formatCount(subCount, 'sub-collection');
	const subNode = subText ? (
		<Link
			to={`/collections/${collectionId}/#sub-collections`}
			className="collection-card__meta-link"
			aria-label={`View the ${subText} of ${collectionName}`}
		>
			{subText}
		</Link>
	) : null;
	const lead = parentNode || subNode ? (
		<>
			{parentNode}
			{subNode}
			{' · '}
		</>
	) : null;
	const role = roleLabel(collection);
	// The kind tag sits in the slot the role label uses — the role is never
	// shown to the owner, so on their tiles the slot is free; on someone
	// else's tile the kind comes first and the role follows it.
	const kind = kindLabel(collection);
	const roleNode = (kind || (role && role !== 'Owner')) ? (
		<span className="collection-card__tags">
			{kind ? <span className="collection-card__role collection-card__kind">{kind}</span> : null}
			{role && role !== 'Owner'
				? <span className="collection-card__role" title="Your access to this collection">{role}</span>
				: null}
		</span>
	) : null;

	// Counts that could not be had (the counts request was refused) are
	// unknown, not zero: the foot says nothing about them, never "Empty".
	const unknown = typeof counts.cards !== 'number' && typeof counts.items !== 'number'
		&& typeof counts.familyItems !== 'number';
	if (unknown) {
		return (
			<>
				{parentNode || subNode ? (
					<span className="collection-card__meta-text">
						{parentNode}
						{subNode}
					</span>
				) : null}
				{roleNode}
			</>
		);
	}

	if (!cardsCount && !itemsCount) {
		return (
			<>
				<span className="collection-card__meta-text">
					{lead}
					Empty
					{canManage(collection) && collectionId ? (
						<>
							{' · '}
							<Link
								to={`/collections/${collectionId}/cards`}
								className="collection-card__meta-link"
							>
								Add a card
							</Link>
						</>
					) : null}
				</span>
				{roleNode}
			</>
		);
	}

	const cardsText = formatCount(cardsCount, 'card');
	const itemsText = formatCount(itemsCount, 'item');
	return (
		<>
			<span className="collection-card__meta-text">
				{lead}
				{cardsText ? (
					<Link
						to={collectionCardsPath(collectionId)}
						className="collection-card__meta-link"
						aria-label={`View the ${cardsText} in ${collectionName}`}
					>
						{cardsText}
					</Link>
				) : null}
				{cardsText && itemsText ? ' · ' : null}
				{itemsText}
			</span>
			{roleNode}
		</>
	);
};

const CollectionCard = ({ collection, counts, onUnfollowed, family }) => {
	// Per-user favorite: the context saves optimistically and rolls back on
	// failure, and every consumer (the Favorites section included) re-renders.
	const { isFavoriteCollection, toggleCollectionFavorite } = useFavorites();
	const navigate = useNavigate();
	const [shareOpen, setShareOpen] = useState(false);
	const [newSubOpen, setNewSubOpen] = useState(false);
	const isFavorite = isFavoriteCollection(collection?._id);
	const { message } = App.useApp();
	// A global collection read only as a consumer: it is here because it is
	// followed, so its ⋯ offers Unfollow, and it carries no favorite star.
	const consumer = isGlobalConsumer(collection);

	const handleToggleFavorite = () => {
		if (!collection?._id) return;
		toggleCollectionFavorite(collection._id, collection);
	};

	const detail = collection?.detail || collection?.description || '';
	const collectionName = collection?.name || 'Untitled collection';
	const collectionId = collection?._id || '';
	// The whole tile is the click target, landing on the items list — the
	// surface you almost always want a collection open to.
	const collectionLink = collectionId ? `/collections/${collectionId}/items` : '#';

	// Rebuilt when the collection's short code arrives (hooks/useRecordCodes).
	const codesVersion = useRecordCodesVersion();
	const shareUrl = useMemo(() => buildCollectionShareUrl(collectionId), [collectionId, codesVersion]); // eslint-disable-line react-hooks/exhaustive-deps
	const downloadName = useMemo(() => {
		const slug = slugify(collectionName);
		return `keepr-collection${slug ? `-${slug}` : ''}${collectionId ? `-${collectionId}` : ''}`;
	}, [collectionName, collectionId]);

	// Identity color reaches exactly two places: the icon chip and the 3px
	// left edge the CSS derives from this variable.
	const accentStyle = collection?.color ? { '--collection-accent': collection.color } : undefined;

	// The platform taxonomy: navigate · share · destructive (the delete entry
	// is the door to the Danger zone's typed confirmation, for its managers:
	// KPR-1267).
	const overflowMenu = buildActionMenu([
		{
			key: 'items',
			group: 'navigate',
			glyph: 'items',
			label: 'View items',
			onClick: () => navigate(`/collections/${collectionId}/items`),
		},
		{
			key: 'home',
			group: 'navigate',
			glyph: 'home',
			label: 'Collection home',
			onClick: () => navigate(`/collections/${collectionId}/`),
		},
		canManage(collection) ? {
			key: 'settings',
			group: 'navigate',
			glyph: 'settings',
			label: 'Settings',
			onClick: () => navigate(`/collections/${collectionId}/form`),
		} : null,
		canAddSubCollection(collection) ? {
			key: 'new-sub-collection',
			group: 'record',
			glyph: 'collection',
			label: 'New sub-collection…',
			onClick: () => setNewSubOpen(true),
		} : null,
		{
			key: 'share',
			group: 'share',
			glyph: 'share',
			label: 'Share collection',
			disabled: !shareUrl,
			onClick: () => setShareOpen(true),
		},
		// A personal collection is never deleted (it leaves only with its
		// account), so it offers no door to a Danger zone it does not have.
		consumer && collection?.followed ? {
			key: 'unfollow',
			group: 'record',
			icon: <BOOKMARK />,
			label: 'Unfollow',
			onClick: async () => {
				try {
					await unfollowCollection(collectionId);
					message.success(`You no longer follow ${collectionName}.`);
					if (typeof onUnfollowed === 'function') onUnfollowed(collectionId);
				} catch (e) {
					message.error(publicationRefusal(e));
				}
			},
		} : null,
		canManage(collection) && !isPersonal(collection) && !collection?.publication?.state?.match(/^(published|retiring)$/) ? {
			key: 'delete',
			group: 'destructive',
			glyph: 'delete',
			label: 'Delete collection…',
			onClick: () => navigate(`/collections/${collectionId}/form?tab=danger`),
		} : null,
	]);

	return (
		<div className="collection-card" style={accentStyle}>
			<div className="collection-card__top">
				{/* The plain title is the tile's link; the stretched-link CSS
				    grows its anchor to cover the whole tile. Hover controls
				    sit above it. */}
				<RecordBadge
					kind="collection"
					record={collection}
					variant="plain"
					size="md"
					title={collectionName}
					to={collectionLink}
					className="collection-card__link"
				/>
				{/* Hover/focus controls: the favorite flag and the one ⋯ menu. */}
				<div className="collection-card__hover-actions">
					{/* No star on the personal collection: it is always the
					    rail's first pin already, and cannot be unpinned. */}
					{isPersonal(collection) || consumer ? null : (
						<CollectionFavoriteButton
							className="collection-actions__button collection-card__favorite"
							disabled={!collectionId}
							isFavorite={isFavorite}
							onToggleFavorite={handleToggleFavorite}
						/>
					)}
					<Dropdown trigger={['click']} placement="bottomRight" menu={overflowMenu || { items: [] }}>
						<Button
							type="text"
							aria-label="Collection actions"
							className="collection-actions__button"
							icon={<KEBAB className="medium-icon" />}
							disabled={!collectionId}
						/>
					</Dropdown>
				</div>
			</div>
			{detail ? <p className="collection-card__detail">{detail}</p> : null}

			<div className="collection-card__footer meta-line">
				<CollectionMeta
					collection={collection}
					counts={counts || {}}
					collectionId={collectionId}
					collectionName={collectionName}
					family={family}
				/>
			</div>

			{newSubOpen ? (
				<NewSubCollectionModal open={newSubOpen} onClose={() => setNewSubOpen(false)} parent={collection} />
			) : null}

			<ShareModal
				open={shareOpen}
				onClose={() => setShareOpen(false)}
				modalTitle="Share collection"
				preview={(
					<RecordIdentity
						kicker="Collection"
						badge={<RecordBadge kind="collection" record={collection} title={collectionName} />}
					/>
				)}
				shareUrl={shareUrl}
				downloadFileName={downloadName}
			/>
		</div>
	);
};
